import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button, Card, Result, Spin, Tag, Typography, message } from "antd";
import type {
  AcceptInviteResponse,
  InviteInfoResponse,
  LoginResponse,
  SetupTotpResponse,
} from "@coco-mailrelay/shared-types";
import { apiRequest } from "../lib/apiRequest";
import { errorMessage } from "../lib/errors";
import { useAuthStore } from "../store/auth.store";
import TotpSetup from "../components/TotpSetup";

const { Title, Text } = Typography;

type Stage = "info" | "totp_setup";

export default function AcceptInvitePage(): JSX.Element {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const navigate = useNavigate();
  const login = useAuthStore((s) => s.login);

  const [stage, setStage] = useState<Stage>("info");
  const [checking, setChecking] = useState(true);
  const [loading, setLoading] = useState(false);
  const [info, setInfo] = useState<InviteInfoResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [pendingToken, setPendingToken] = useState("");
  const [setup, setSetup] = useState<SetupTotpResponse | null>(null);

  useEffect(() => {
    if (!token) {
      setError("Missing invitation token");
      setChecking(false);
      return;
    }
    apiRequest<InviteInfoResponse>("/auth/invite-info", { params: { token } })
      .then(setInfo)
      .catch((err) => setError(errorMessage(err, "Invitation is invalid or expired")))
      .finally(() => setChecking(false));
  }, [token]);

  const handleAccept = async (): Promise<void> => {
    if (!token) return;
    setLoading(true);
    try {
      const accepted = await apiRequest<AcceptInviteResponse>("/auth/accept-invite", {
        method: "POST",
        body: { token },
      });
      const setupRes = await apiRequest<SetupTotpResponse>("/auth/setup-totp", {
        method: "POST",
        authToken: accepted.pendingToken,
      });
      setSetup(setupRes);
      setPendingToken(setupRes.pendingToken);
      setStage("totp_setup");
    } catch (err) {
      message.error(errorMessage(err, "Failed to accept invitation"));
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmSetup = async (totpCode: string): Promise<void> => {
    setLoading(true);
    try {
      const res = await apiRequest<LoginResponse>("/auth/confirm-totp-setup", {
        method: "POST",
        body: { totpCode },
        authToken: pendingToken,
      });
      login(res.token, res.member);
      message.success("Welcome aboard");
      navigate("/tenants");
    } catch (err) {
      message.error(errorMessage(err, "Setup failed"));
    } finally {
      setLoading(false);
    }
  };

  if (checking) {
    return (
      <div style={pageStyle}>
        <Spin size="large" tip="Validating invitation..." />
      </div>
    );
  }

  if (error) {
    return (
      <div style={pageStyle}>
        <Result
          status="error"
          title="Invitation invalid"
          subTitle={error}
          extra={
            <Button type="primary" onClick={() => navigate("/login")}>
              Go to sign in
            </Button>
          }
        />
      </div>
    );
  }

  return (
    <div style={pageStyle}>
      <Card style={{ width: 420, borderRadius: 12 }}>
        <div style={{ textAlign: "center", marginBottom: 24 }}>
          <Title level={3} style={{ marginBottom: 4 }}>
            coco-mailrelay
          </Title>
          <Text type="secondary">
            {stage === "info" ? "Accept your invitation" : "Bind your authenticator"}
          </Text>
        </div>

        {stage === "info" && info && (
          <>
            <Card size="small" style={{ marginBottom: 24, background: "#fafafa" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div>
                  <Text type="secondary">Email</Text>
                  <div>
                    <Text strong>{info.email}</Text>
                  </div>
                </div>
                <div style={{ textAlign: "right" }}>
                  <Text type="secondary">Role</Text>
                  <div>
                    <Tag color="blue">{info.role}</Tag>
                  </div>
                </div>
              </div>
            </Card>
            <Button type="primary" loading={loading} block size="large" onClick={handleAccept}>
              Accept and set up TOTP
            </Button>
            <div style={{ textAlign: "center", marginTop: 12 }}>
              <Button type="link" onClick={() => navigate("/login")}>
                Already have an account? Sign in
              </Button>
            </div>
          </>
        )}

        {stage === "totp_setup" && setup && (
          <TotpSetup
            otpauthUri={setup.otpauthUri}
            secret={setup.secret}
            loading={loading}
            submitLabel="Confirm and finish"
            onConfirm={handleConfirmSetup}
          />
        )}
      </Card>
    </div>
  );
}

const pageStyle: React.CSSProperties = {
  minHeight: "100vh",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  background: "#f5f5f5",
};
